export const REPORT_ICON_SVG_MAX_BYTES = 24 * 1024;
export const REPORT_ICON_SVG_MAX_ELEMENTS = 200;

const SVG_NS = 'http://www.w3.org/2000/svg';
const allowedElements = new Set([
  'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
]);
const presentationAttributes = new Set([
  'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'opacity', 'fill-opacity', 'stroke-opacity', 'stroke-miterlimit',
  'fill-rule', 'clip-rule', 'vector-effect', 'style',
]);
const elementAttributes = {
  svg: new Set(['viewBox', 'xmlns', 'width', 'height', 'preserveAspectRatio', ...presentationAttributes]),
  g: new Set(['transform', ...presentationAttributes]),
  path: new Set(['d', 'transform', ...presentationAttributes]),
  rect: new Set(['x', 'y', 'width', 'height', 'rx', 'ry', 'transform', ...presentationAttributes]),
  circle: new Set(['cx', 'cy', 'r', 'transform', ...presentationAttributes]),
  ellipse: new Set(['cx', 'cy', 'rx', 'ry', 'transform', ...presentationAttributes]),
  line: new Set(['x1', 'y1', 'x2', 'y2', 'transform', ...presentationAttributes]),
  polyline: new Set(['points', 'transform', ...presentationAttributes]),
  polygon: new Set(['points', 'transform', ...presentationAttributes]),
};
const numericAttributes = new Set([
  'x', 'y', 'width', 'height', 'rx', 'ry', 'cx', 'cy', 'r',
  'x1', 'y1', 'x2', 'y2', 'stroke-width', 'opacity', 'fill-opacity',
  'stroke-opacity', 'stroke-miterlimit',
]);
const numberPattern = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;
const pathPattern = /^[MmZzLlHhVvCcSsQqTtAa0-9+.,eE\s-]+$/;
const pointsPattern = /^[0-9+.,eE\s-]+$/;
const paintPattern = /^(?:none|currentColor|#[0-9a-fA-F]{3,8}|rgba?\([0-9.,%\s]+\))$/;
const transformPattern = /^(?:(?:matrix|translate|scale|rotate|skewX|skewY)\([0-9+.,eE\s-]+\)\s*)+$/;

function normalizeViewBox(value) {
  const parts = String(value || '').trim().split(/[\s,]+/);
  if (parts.length !== 4) return '';
  const numbers = parts.map(Number);
  if (!numbers.every(Number.isFinite) || numbers[2] <= 0 || numbers[3] <= 0) return '';
  if (numbers.some(number => Math.abs(number) > 1000000)) return '';
  return numbers.join(' ');
}

function normalizeDimension(value) {
  const normalized = String(value || '').trim().replace(/px$/i, '');
  const number = Number(normalized);
  if (!Number.isFinite(number) || number <= 0 || number > 1000000) return '';
  return String(number);
}

function validateStyle(value) {
  const declarations = String(value || '').split(';').map(item => item.trim()).filter(Boolean);
  if (!declarations.length) return false;
  return declarations.every(declaration => {
    const separator = declaration.indexOf(':');
    if (separator <= 0 || declaration.indexOf(':', separator + 1) !== -1) return false;
    const property = declaration.slice(0, separator).trim();
    const propertyValue = declaration.slice(separator + 1).trim();
    return property !== 'style'
      && presentationAttributes.has(property)
      && validateAttribute(property, propertyValue);
  });
}

function validateAttribute(name, value) {
  if (!value || value.length > 8192 || /url\s*\(|javascript:|data:|[<>]/i.test(value)) {
    return false;
  }
  if (numericAttributes.has(name)) return numberPattern.test(value);
  if (name === 'd') return pathPattern.test(value);
  if (name === 'points') return pointsPattern.test(value);
  if (name === 'fill' || name === 'stroke') return paintPattern.test(value);
  if (name === 'transform') return transformPattern.test(value);
  if (name === 'style') return validateStyle(value);
  if (name === 'stroke-linecap') return /^(?:butt|round|square)$/.test(value);
  if (name === 'stroke-linejoin') return /^(?:miter|round|bevel)$/.test(value);
  if (name === 'fill-rule' || name === 'clip-rule') return /^(?:nonzero|evenodd)$/.test(value);
  if (name === 'vector-effect') return /^(?:none|non-scaling-stroke)$/.test(value);
  if (name === 'preserveAspectRatio') return /^(?:none|x(?:Min|Mid|Max)Y(?:Min|Mid|Max)(?:\s+(?:meet|slice))?)$/.test(value);
  if (name === 'xmlns') return value === SVG_NS;
  return name === 'viewBox' && Boolean(normalizeViewBox(value));
}

function removeHarmlessMetadata(element) {
  Array.from(element.childNodes).forEach(node => {
    if (node.nodeType === Node.COMMENT_NODE || node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
      element.removeChild(node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === 'title' || node.tagName === 'desc') {
      element.removeChild(node);
      return;
    }
    removeHarmlessMetadata(node);
  });
}

/**
 * Valida y normaliza el subconjunto SVG admitido para iconos de reporte.
 * Solo admite estilos de presentación en línea previamente validados; no
 * admite eventos, referencias, imágenes ni contenido textual.
 */
export function validateAndNormalizeReportIconSvg(raw) {
  const source = String(raw || '').trim();
  if (!source) return '';
  if (new Blob([source]).size > REPORT_ICON_SVG_MAX_BYTES) {
    throw new Error('El SVG supera el máximo permitido de 24 KB.');
  }

  const documentNode = new DOMParser().parseFromString(source, 'image/svg+xml');
  if (documentNode.querySelector('parsererror')) {
    throw new Error('El archivo no contiene un SVG válido.');
  }
  const root = documentNode.documentElement;
  if (!root || root.tagName !== 'svg' || (root.namespaceURI && root.namespaceURI !== SVG_NS)) {
    throw new Error('El elemento raíz debe ser <svg>.');
  }
  const width = normalizeDimension(root.getAttribute('width'));
  const height = normalizeDimension(root.getAttribute('height'));
  const normalizedViewBox = normalizeViewBox(root.getAttribute('viewBox'))
    || (width && height ? `0 0 ${width} ${height}` : '');
  if (!normalizedViewBox) {
    throw new Error('El SVG debe incluir un viewBox o dimensiones numéricas válidas.');
  }

  removeHarmlessMetadata(root);
  root.setAttribute('viewBox', normalizedViewBox);
  root.removeAttribute('width');
  root.removeAttribute('height');
  root.removeAttribute('preserveAspectRatio');

  const elements = [root, ...root.querySelectorAll('*')];
  if (elements.length > REPORT_ICON_SVG_MAX_ELEMENTS) throw new Error('El SVG contiene demasiados elementos.');
  if (elements.length === 1) throw new Error('El SVG no contiene ninguna figura.');

  elements.forEach(element => {
    const tag = element.tagName;
    if (!allowedElements.has(tag)) throw new Error(`El elemento <${tag}> no está permitido.`);
    Array.from(element.attributes).forEach(attribute => {
      if (attribute.name.includes(':') || !elementAttributes[tag].has(attribute.name)) {
        throw new Error(`El atributo ${attribute.name} no está permitido.`);
      }
      if (!validateAttribute(attribute.name, attribute.value)) {
        throw new Error(`El valor del atributo ${attribute.name} no es válido.`);
      }
    });
    Array.from(element.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) return;
      if (node.nodeType !== Node.ELEMENT_NODE) {
        throw new Error('El SVG contiene texto, comentarios o instrucciones no permitidas.');
      }
    });
  });

  if (!root.getAttribute('xmlns')) root.setAttribute('xmlns', SVG_NS);
  return new XMLSerializer().serializeToString(root);
}

export async function readReportIconSvgFile(file) {
  if (!file || !/\.svg$/i.test(file.name || '')) {
    throw new Error('Solo se permiten archivos con extensión .svg.');
  }
  if (file.size > REPORT_ICON_SVG_MAX_BYTES) {
    throw new Error('El SVG supera el máximo permitido de 24 KB.');
  }
  return validateAndNormalizeReportIconSvg(await file.text());
}
